import { defineKey as T } from '@ha/intl';

import {
  ListingCostPayableAtValues,
  ListingCostPayableByValues,
} from '@ha/api/v2/types/Listing';

import {
  CostsUtilityKeys,
  CostsUtilityCompatibilityKey,
  CostsAdditionalKeys,
  CostsDepositKeys,
  CostsOptionalKeys,
} from 'ha/models/ListingCosts/types';

import { CostDefaultOptions } from './types';

export const TRANSLATIONS_COST_OPTIONS_BY_ATTRIBUTE: {
  default: { [K in CostDefaultOptions]: string };
  payableBy: { [K in ListingCostPayableByValues]: string };
  payableAt: { [K in ListingCostPayableAtValues]: string };
  isEstimated: { [K in CostDefaultOptions]: string };
  required: { [K in CostDefaultOptions]: string };
} = {
  default: {
    [CostDefaultOptions.TRUE]: T('StructuredCosts.create.included'),
    [CostDefaultOptions.FALSE]: T('StructuredCosts.create.excluded'),
    [CostDefaultOptions.NON_APPLICABLE]: T(
      'StructuredCosts.create.nonapplicable',
    ),
  },
  payableBy: {
    [ListingCostPayableByValues.INCLUDED_IN_RENT]: T(
      'StructuredCosts.create.included',
    ),
    [ListingCostPayableByValues.TENANT]: T('StructuredCosts.create.excluded'),
    [ListingCostPayableByValues.NOT_APPLICABLE]: T(
      'StructuredCosts.create.nonapplicable',
    ),
    [ListingCostPayableByValues.UNKNOWN]: T(
      'listings.costs.utilities.not_known',
    ),
  },
  payableAt: {
    [ListingCostPayableAtValues.MOVE_IN]: T(
      'StructuredCosts.create.frequency.movein',
    ),
    [ListingCostPayableAtValues.MOVE_OUT]: T(
      'StructuredCosts.create.frequency.moveout',
    ),
    [ListingCostPayableAtValues.ONE_OFF]: T(
      'StructuredCosts.create.frequency.oneoff',
    ),
    [ListingCostPayableAtValues.MONTHLY]: T(
      'StructuredCosts.create.frequency.monthly',
    ),
    [ListingCostPayableAtValues.BIMONTHLY]: T(
      'StructuredCosts.frequency.every_2_months',
    ),
    [ListingCostPayableAtValues.BIWEEKLY]: T(
      'StructuredCosts.create.frequency.biweekly',
    ),
    [ListingCostPayableAtValues.WEEKLY]: T(
      'StructuredCosts.create.frequency.weekly',
    ),
  },
  isEstimated: {
    [CostDefaultOptions.TRUE]: T('StructuredCosts.create.estimated'),
    [CostDefaultOptions.FALSE]: T('StructuredCosts.create.fixed'),
    [CostDefaultOptions.NON_APPLICABLE]: T(
      'StructuredCosts.create.nonapplicable',
    ),
  },
  required: {
    [CostDefaultOptions.TRUE]: T('StructuredCosts.required'),
    [CostDefaultOptions.FALSE]: T('StructuredCosts.optional'),
    [CostDefaultOptions.NON_APPLICABLE]: T(
      'StructuredCosts.create.nonapplicable',
    ),
  },
};

export const TRANSLATIONS_COST_LABEL_BY_KEY: {
  [key: string]: string;
} = {
  [CostsUtilityKeys.BROADCASTING]: T(
    'StructuredCosts.create.utilitycosts.broadcasting_fee',
  ),
  [CostsUtilityKeys.ELECTRICITY_BILL]: T('Electricity'),
  [CostsUtilityKeys.GAS_BILL]: T('Gas'),
  [CostsUtilityKeys.INTERNET_BILL]: T('Internet'),
  [CostsUtilityKeys.WATER_BILL]: T('Water'),
  [CostsAdditionalKeys.ADMINISTRATION_FEE]: T(
    'StructuredCosts.create.additionalcosts.administrationfee',
  ),
  [CostsAdditionalKeys.CLEANING_FEE]: T(
    'StructuredCosts.create.additionalcosts.cleaning',
  ),
  [CostsAdditionalKeys.OTHER_ADDITIONAL_COSTS]: T(
    'StructuredCosts.create.additionalcosts.other',
  ),
  [CostsDepositKeys.BEDDING_DEPOSIT]: T(
    'StructuredCosts.create.deposits.bedding',
  ),
  [CostsDepositKeys.OTHER_DEPOSIT]: T('StructuredCosts.create.deposits.other'),
  [CostsDepositKeys.SECURITY_DEPOSIT]: T(
    'StructuredCosts.create.deposits.securitydeposit',
  ),
  [CostsDepositKeys.TOWEL_DEPOSIT]: T('StructuredCosts.create.deposits.towels'),
  [CostsOptionalKeys.BIKE_RENT]: T(
    'StructuredCosts.create.optionalservices.bike_rent',
  ),
  [CostsOptionalKeys.CLEANING_SERVICE]: T(
    'StructuredCosts.create.optionalservices.cleaning',
  ),
  [CostsOptionalKeys.EARLY_MOVE_IN]: T(
    'StructuredCosts.create.optionalservices.earlymovein',
  ),
  [CostsOptionalKeys.EARLY_MOVE_OUT]: T(
    'StructuredCosts.create.optionalservices.earlymoveout',
  ),
  [CostsOptionalKeys.FINAL_CLEANING]: T(
    'StructuredCosts.create.optionalservices.finalcleaning',
  ),
  [CostsOptionalKeys.FULL_BOARD]: T(
    'StructuredCosts.create.optionalservices.full_board',
  ),
  [CostsOptionalKeys.GYM]: T('StructuredCosts.create.optionalservices.gym'),
  [CostsOptionalKeys.HALF_BOARD]: T(
    'StructuredCosts.create.optionalservices.half_board',
  ),
  [CostsOptionalKeys.LATE_MOVE_IN]: T(
    'StructuredCosts.create.optionalservices.latemovein',
  ),
  [CostsOptionalKeys.LATE_MOVE_OUT]: T(
    'StructuredCosts.create.optionalservices.latemoveout',
  ),
  [CostsOptionalKeys.OTHER_OPTIONAL_COSTS]: T(
    'StructuredCosts.create.optionalservices.otheroptionalcosts',
  ),
  [CostsOptionalKeys.PARKING]: T(
    'StructuredCosts.create.optionalservices.parking',
  ),
  [CostsOptionalKeys.TOWEL_BEDDING]: T(
    'StructuredCosts.create.optionalservices.towels_bedding',
  ),
  [CostsOptionalKeys.OVERNIGHT_GUESTS]: T(
    'StructuredCosts.create.optionalservices.overnightguests',
  ),
  [CostsOptionalKeys.END_EARLY]: T(
    'StructuredCosts.create.optionalservices.end_early',
  ),
  [CostsUtilityCompatibilityKey.UTILITY_BILLS]: T('Estimated bills'),
};
